import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetUsers, useDownloadUsers } from '../../data';
import Card from '../../../../shared/Card';
import Placeholder from '../../../../shared/Placeholder';
import useSort from '../../../../shared/Sort/useSort';
import { TopBar } from '../shared/TopBar.jsx';
import UsersTable from './UsersTable.jsx';
import { ActionCableContext } from '../../../../lib/actionCableContext';
import { useNotification } from '../../../../shared/Notification/useNotification';

export default function AllUsers() {
  const [showAll, setShowAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [nameQuery, setNameQuery] = useState('');
  const { sortingStatus, changeSort } = useSort();
  const { t } = useTranslation('users');
  const onPageChange = data => setPage(data.selected + 1);
  const cable = useContext(ActionCableContext);
  const { showSuccess } = useNotification();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const userUuid = urlParams.get('user_id');
  const userName = urlParams.get('user_name');

  const { data, isLoading, itemsPerPage } = useGetUsers({
    page,
    ...sortingStatus,
    nameQuery,
    isPaginated: showAll,
  });

  const { downloadUsers } = useDownloadUsers();

  const handleDownloadUsersClick = () => {
    setDownloading(true);
    downloadUsers();
  };

  const search = {
    nameQuery,
    executeSearch: value => {
      setNameQuery(value);
      setPage(1);
    },
    clearSearch: () => {
      setNameQuery('');
      setPage(1);
    },
  };

  useEffect(() => {
    cable.subscriptions.create(
      {
        channel: 'ExportUsersListChannel',
      },
      {
        received: response => {
          showSuccess({
            title: t('allUsers.reportGeneratedTitle'),
            content: t('allUsers.reportGeneratedContent', {
              email: response.email,
            }),
          });
          setDownloading(false);
        },
      },
    );
  }, [cable, showSuccess, t, setDownloading]);

  useEffect(() => {
    setNameQuery(userName);
  }, [userName]);

  return (
    <>
      <TopBar />
      <Card
        title={t('allUsers.allUsers')}
        items={itemsPerPage}
        onClick={() => setShowAll(s => !s)}
        canShowAll={true}
        canDownload={true}
        downloadButtonText={t('allUsers.downloadButton')}
        onClickDownload={handleDownloadUsersClick}
        isDownloading={downloading}
        isShowingAll={showAll}
        isLoading={isLoading}
        pagy={data.pagy}
        search={search}
        onPageChange={onPageChange}
        margin="0 24px"
      >
        {data.users.length ? (
          <UsersTable
            data={data.users}
            sortingStatus={sortingStatus}
            changeSort={changeSort}
            openModal={userUuid}
          />
        ) : (
          <Placeholder />
        )}
      </Card>
    </>
  );
}
